import React, { useState, useEffect, useCallback } from 'react';
import {
    FileText, Save, Loader2, AlertCircle, Check, Eye, EyeOff,
    X, HelpCircle, Copy, ChevronDown, ChevronUp
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    fetchMarketingBrief,
    saveMarketingBrief,
    BRIEF_MAX_CHARS,
    BRIEF_TEMPLATE
} from '../services/MarketingService';

// Safe markdown preview renderer using React elements (no dangerouslySetInnerHTML)
const MarkdownPreview = ({ content }) => {
    if (!content) return null;

    // Parse and render markdown safely using React elements
    const renderContent = () => {
        const lines = content.split('\n');
        const elements = [];
        let listItems = [];
        let inList = false;

        const flushList = () => {
            if (listItems.length > 0) {
                elements.push(
                    <ul key={`list-${elements.length}`} className="my-2 ml-4 list-disc space-y-1">
                        {listItems}
                    </ul>
                );
                listItems = [];
            }
            inList = false;
        };

        lines.forEach((line, idx) => {
            const trimmedLine = line.trim();

            // Headers
            if (trimmedLine.startsWith('### ')) {
                flushList();
                elements.push(
                    <h3 key={idx} className="text-lg font-semibold text-slate-900 dark:text-white mt-4 mb-2">
                        {trimmedLine.slice(4)}
                    </h3>
                );
                return;
            }
            if (trimmedLine.startsWith('## ')) {
                flushList();
                elements.push(
                    <h2 key={idx} className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3">
                        {trimmedLine.slice(3)}
                    </h2>
                );
                return;
            }
            if (trimmedLine.startsWith('# ')) {
                flushList();
                elements.push(
                    <h1 key={idx} className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
                        {trimmedLine.slice(2)}
                    </h1>
                );
                return;
            }

            // Blockquotes
            if (trimmedLine.startsWith('> ')) {
                flushList();
                elements.push(
                    <blockquote key={idx} className="border-l-4 border-violet-500 pl-4 italic text-slate-600 dark:text-slate-400 my-2">
                        {trimmedLine.slice(2)}
                    </blockquote>
                );
                return;
            }

            // Horizontal rules
            if (trimmedLine === '---') {
                flushList();
                elements.push(
                    <hr key={idx} className="border-slate-200 dark:border-slate-700 my-4" />
                );
                return;
            }

            // List items
            if (trimmedLine.startsWith('- ')) {
                inList = true;
                listItems.push(
                    <li key={idx} className="text-slate-700 dark:text-slate-300">
                        {renderInlineMarkdown(trimmedLine.slice(2))}
                    </li>
                );
                return;
            }

            // Empty lines
            if (!trimmedLine) {
                flushList();
                return;
            }

            // Regular paragraphs
            flushList();
            elements.push(
                <p key={idx} className="my-2 text-slate-700 dark:text-slate-300">
                    {renderInlineMarkdown(trimmedLine)}
                </p>
            );
        });

        flushList();
        return elements;
    };

    // Render inline markdown (bold, italic, hashtags)
    const renderInlineMarkdown = (text) => {
        const parts = [];
        let remaining = text;
        let key = 0;

        while (remaining) {
            // Bold
            const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
            if (boldMatch && boldMatch.index === 0) {
                parts.push(<strong key={key++} className="font-semibold">{boldMatch[1]}</strong>);
                remaining = remaining.slice(boldMatch[0].length);
                continue;
            }

            // Hashtags
            const hashtagMatch = remaining.match(/^#(\w+)/);
            if (hashtagMatch) {
                parts.push(
                    <span key={key++} className="text-violet-600 dark:text-violet-400">
                        #{hashtagMatch[1]}
                    </span>
                );
                remaining = remaining.slice(hashtagMatch[0].length);
                continue;
            }

            // Find next special character
            const nextSpecial = remaining.search(/\*\*|#\w/);
            if (nextSpecial === -1) {
                parts.push(remaining);
                break;
            }
            if (nextSpecial > 0) {
                parts.push(remaining.slice(0, nextSpecial));
                remaining = remaining.slice(nextSpecial);
            } else {
                parts.push(remaining[0]);
                remaining = remaining.slice(1);
            }
        }

        return parts;
    };

    return <div className="space-y-1">{renderContent()}</div>;
};

// Example brief component
const ExampleBrief = ({ onUseTemplate }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const exampleContent = `# CloudMortgage.io Marketing Brief

> This is an example of a well-structured marketing brief. Use this as a template for your own brief.

## Company Overview

CloudMortgage.io is an AI-powered mortgage underwriting platform that automates mortgage document review, analysis, and compliance reporting while keeping human underwriters in full control (human-in-the-loop, no auto approvals).

## Core Value Proposition

- Reduces underwriting time from 12-30 hours per loan file to minutes
- Cuts underwriting labor cost from $150-$840 per loan to under $1 per case
- Delivers audit-ready, source-linked compliance reports
- Achieves ~98% accuracy with consistent quality

## Target Customers

**Primary:**
- Independent underwriters
- Small to mid-size brokerages
- Credit unions
- Regional banks

**Secondary:**
- Large mortgage servicers
- Wholesale lenders

## Brand Voice & Messaging

**Taglines:**
- "AI-Powered Underwriting. Human Control."
- "From 30 Hours to 30 Minutes."

**Key Themes:**
- Speed
- Cost savings
- Accuracy
- Compliance readiness

## Hashtags

#CloudMortgage #MortgageTech #AIinLending #UnderwritingAutomation`;

    return (
        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-violet-500" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        View Example Brief
                    </span>
                </div>
                {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-slate-400" />
                ) : (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                )}
            </button>
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="px-4 pb-4 border-t border-slate-200 dark:border-slate-700">
                            <div className="mt-3 max-h-64 overflow-y-auto bg-white dark:bg-slate-900 rounded-lg p-4 text-sm">
                                <pre className="whitespace-pre-wrap text-slate-600 dark:text-slate-400 font-mono text-xs">
                                    {exampleContent}
                                </pre>
                            </div>
                            <button
                                onClick={() => onUseTemplate(exampleContent)}
                                className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded-lg text-sm font-medium hover:bg-violet-200 dark:hover:bg-violet-900/50 transition-colors"
                            >
                                <Copy className="w-4 h-4" />
                                Use This Template
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const MarketingBriefEditor = ({ isOpen, onClose }) => {
    const [content, setContent] = useState('');
    const [originalContent, setOriginalContent] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);

    const hasChanges = content !== originalContent;
    const charCount = content.length;
    const isOverLimit = charCount > BRIEF_MAX_CHARS;

    // Load brief on mount
    useEffect(() => {
        if (!isOpen) return;

        const loadBrief = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const data = await fetchMarketingBrief();
                const briefContent = data?.content || '';
                setContent(briefContent);
                setOriginalContent(briefContent);
                if (data?.updated_at) {
                    setLastSaved(new Date(data.updated_at));
                }
            } catch (err) {
                console.error('Failed to load brief:', err);
                setError('Failed to load your marketing brief. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        loadBrief();
    }, [isOpen]);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!hasChanges || isOverLimit) return;

        setIsSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const data = await saveMarketingBrief(content);
            setOriginalContent(content);
            setLastSaved(new Date(data.updated_at));
            setSuccessMessage('Brief saved successfully!');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            console.error('Failed to save brief:', err);
            setError(err.message || 'Failed to save. Please try again.');
        } finally {
            setIsSaving(false);
        }
    }, [content, hasChanges, isOverLimit]);

    // Handle template use
    const handleUseTemplate = (template) => {
        if (content && !window.confirm('This will replace your current content. Continue?')) {
            return;
        }
        setContent(template);
    };

    // Handle use basic template
    const handleUseBasicTemplate = () => {
        if (content && !window.confirm('This will replace your current content. Continue?')) {
            return;
        }
        setContent(BRIEF_TEMPLATE);
    };

    // Format last saved time
    const formatLastSaved = (date) => {
        if (!date) return null;
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins} min ago`;
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    };

    if (!isOpen) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                                Marketing Brief
                            </h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                Tell the AI about your business for better marketing advice
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex flex-col">
                    {isLoading ? (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="text-center">
                                <Loader2 className="w-8 h-8 text-violet-500 animate-spin mx-auto mb-3" />
                                <p className="text-sm text-slate-500 dark:text-slate-400">Loading your brief...</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Toolbar */}
                            <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setShowPreview(!showPreview)}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                            showPreview
                                                ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                        }`}
                                    >
                                        {showPreview ? (
                                            <>
                                                <EyeOff className="w-4 h-4" />
                                                Hide Preview
                                            </>
                                        ) : (
                                            <>
                                                <Eye className="w-4 h-4" />
                                                Show Preview
                                            </>
                                        )}
                                    </button>
                                    {!content && (
                                        <button
                                            onClick={handleUseBasicTemplate}
                                            className="flex items-center gap-2 px-3 py-1.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition-colors"
                                        >
                                            <Copy className="w-4 h-4" />
                                            Use Template
                                        </button>
                                    )}
                                </div>
                                <div className="flex items-center gap-4">
                                    {lastSaved && (
                                        <span className="text-xs text-slate-400">
                                            Last saved: {formatLastSaved(lastSaved)}
                                        </span>
                                    )}
                                    <span className={`text-sm font-medium ${
                                        isOverLimit
                                            ? 'text-red-500'
                                            : charCount > BRIEF_MAX_CHARS * 0.9
                                                ? 'text-amber-500'
                                                : 'text-slate-400'
                                    }`}>
                                        {charCount.toLocaleString()} / {BRIEF_MAX_CHARS.toLocaleString()}
                                    </span>
                                </div>
                            </div>

                            {/* Editor Area */}
                            <div className="flex-1 overflow-hidden flex">
                                {/* Textarea */}
                                <div className={`flex-1 flex flex-col ${showPreview ? 'w-1/2 border-r border-slate-200 dark:border-slate-700' : 'w-full'}`}>
                                    <textarea
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        placeholder="Describe your company, products, target audience, brand voice, and key messages..."
                                        className="flex-1 w-full p-6 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none resize-none font-mono text-sm leading-relaxed"
                                        style={{ minHeight: '300px' }}
                                    />
                                </div>

                                {/* Preview */}
                                {showPreview && (
                                    <div className="w-1/2 overflow-y-auto p-6 bg-slate-50 dark:bg-slate-800/30">
                                        {content ? (
                                            <MarkdownPreview content={content} />
                                        ) : (
                                            <p className="text-sm text-slate-400 italic">
                                                Start typing to see a preview...
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Empty State / Example */}
                            {!content && (
                                <div className="px-6 pb-4">
                                    <ExampleBrief onUseTemplate={handleUseTemplate} />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-2">
                        {error && (
                            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                                <AlertCircle className="w-4 h-4" />
                                {error}
                            </div>
                        )}
                        {successMessage && (
                            <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                                <Check className="w-4 h-4" />
                                {successMessage}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!hasChanges || isOverLimit || isSaving || isLoading}
                            className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-lg text-sm font-medium hover:from-violet-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    Save Brief
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default MarketingBriefEditor;
